/* eslint-disable prettier/prettier */
import React, { useState, useEffect, useCallback, useMemo } from 'react';

import Header from 'components/AppBar';
import Loader from 'components/Loader';
import ModalHeader from 'components/ModalHeader';
import { Desktop, Mobile } from 'components/Responsive';
import StackAllianceCards from 'components/StackAllianceCards';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import useUserAccount from 'hooks/useUserAccount';
import { GroupedListModel } from 'models/groupedList';
import { fetchGetAllAlliances, Alliance, Loyalty } from 'services/loyaltiesService';
import { extractErrorMessage } from 'utils/generic';

import AddLoyalty from './components/AddLoyalty';
import EditLoyalty from './components/EditLoyalty';
import GroupedLoyaltyList from './components/GroupedLoyaltyList';
import { ButtonAddContainer, Container, MainContainer } from './style';
import FloatingButton from 'components/FloatingButton';

interface Props {
  userId?: string;
  loyalties: Loyalty[];
  isLoading: boolean;
  onAddLoyalty: (loyalty: Loyalty) => Promise<void>;
  onDeleteLoyalty: (id: string) => Promise<void>;
  onUpdateLoyalty: (loyalty: Loyalty, refetch?: boolean) => Promise<void>;
  onClose: () => void;
}

const HOTELS_ALLIANCES = ['Accor', 'Marriott', 'Hilton', 'IHG'];

const TravelerLoyalty = ({
  userId,
  onAddLoyalty,
  onDeleteLoyalty,
  onUpdateLoyalty,
  loyalties,
  isLoading,
  onClose,
}: Props) => {
  const toast = useToast();
  const user = useUserAccount();
  const { t } = useStoreTranslation();

  // listControl
  const [showList, setShowList] = useState(false);
  const [allAlliances, setAllAlliances] = useState<GroupedListModel[]>([]);
  const [isLoadingList, setLoadingList] = useState(true);

  // addControl
  const [showAdd, setShowAdd] = useState(false);
  const [selectedAlliance, setSelectedAlliance] = useState<Alliance>({} as Alliance);
  const [isSubmitting, setSubmitting] = useState(false);

  // editControl
  const [showEdit, setShowEdit] = useState(false);
  const [loyaltyEditId, setLoyaltyEditId] = useState<string>();

  useEffect(() => {
    if (!isLoading) {
      setShowList(!(loyalties.length > 0));
    }
  }, [isLoading, loyalties.length]);

  useEffect(() => {
    const fetch = async () => {
      if (!showList || isLoading) {
        setLoadingList(false);
        return;
      }

      try {
        setLoadingList(true);

        const result = await fetchGetAllAlliances();

        let list = result.map(
          (item) =>
            ({
              id: item.id,
              image: `/corporate/images/alliances/${item.logo}`,
              alliance: true,
              otherProps: item,
              name: item.program,
              detail: item.name,
            } as GroupedListModel),
        );

        if (loyalties.length > 0) {
          list = list.filter(
            (alliance) => !loyalties.some((loyalty) => loyalty.loyaltyId.toString() === alliance.id.toString()),
          );
        }
        list = list.filter((alliance) => !HOTELS_ALLIANCES.includes(alliance.id));

        setAllAlliances(list.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')));
      } catch (error) {
        toast.add({
          type: 'danger',
          message: extractErrorMessage(error) || t('defaultError.default'),
        });
      } finally {
        setLoadingList(false);
      }
    };

    fetch();
  }, [showList, loyalties, isLoading, toast, t]);

  const airLoyalties = useMemo(
    () => loyalties.filter((l) => !HOTELS_ALLIANCES.includes(l.loyaltyId) || l.type.toUpperCase() !== 'HOTEL'),
    [loyalties],
  );

  const selectAlliance = useCallback((selected: GroupedListModel) => {
    setSelectedAlliance(selected.otherProps as Alliance);
    setShowList(false);
    setShowAdd(true);
  }, []);

  const handleCancelList = useCallback(() => {
    if (loyalties.length > 0) setShowList(false);
    else onClose();
  }, [loyalties, onClose]);

  const cancelAdd = useCallback(() => {
    const shouldReturnToList = loyalties.length === 0;
    setSelectedAlliance({} as Alliance);
    setShowList(shouldReturnToList);
    setShowAdd(false);
    setShowEdit(false);
  }, [loyalties]);

  const cancelEdit = () => {
    setShowList(false);
    setShowEdit(false);
    setShowAdd(false);
  };

  const handleAdd = useCallback(
    async (loyalty: Loyalty) => {
      setSubmitting(true);
      await onAddLoyalty(loyalty);
      setSubmitting(false);
      setShowList(false);
      setShowAdd(false);
      setShowEdit(false);
    },
    [onAddLoyalty],
  );

  const handleSelectedCard = useCallback((loyalty: Loyalty) => {
    setLoyaltyEditId(loyalty.id);
    setShowList(false);
    setShowAdd(false);
    setShowEdit(true);
  }, []);

  const handleDelete = useCallback(
    async (id: string) => {
      const shouldReturnToList = loyalties.length === 1;
      await onDeleteLoyalty(id);
      setShowEdit(!shouldReturnToList);
      setShowList(shouldReturnToList);
      setShowAdd(false);
    },
    [loyalties.length, onDeleteLoyalty],
  );

  const handleUpdate = useCallback(
    async (loyalty: Loyalty) => {
      setSubmitting(true);
      await onUpdateLoyalty(loyalty, true);
      setSubmitting(false);
      setShowEdit(false);
      setShowList(false);
      setShowAdd(false);
    },
    [onUpdateLoyalty],
  );

  const handleUpdatePreference = useCallback(
    async (loyalty: Loyalty, loyaltyPreference: Loyalty) => {
      setSubmitting(true);
      await onUpdateLoyalty(loyalty, false);
      await onUpdateLoyalty(loyaltyPreference);
      setSubmitting(false);
      setShowEdit(false);
      setShowList(false);
      setShowAdd(false);
    },
    [onUpdateLoyalty],
  );

  return (
    <MainContainer>
      {isLoading || isLoadingList ? (
        <Loader />
      ) : (
        <>
          {!showList && !showAdd && !showEdit && (
            <>
              <Mobile>
                <Header goBack={onClose}>{t('travelerLoyalty.titleList')}</Header>
              </Mobile>

              <Desktop>
                <ModalHeader onClose={onClose} title={t('travelerLoyalty.titleList')} />
              </Desktop>

              <Container>
                <StackAllianceCards onSelect={handleSelectedCard} loyalties={airLoyalties} />
                <FloatingButton zIndex={2} onClick={() => setShowList(true)} className="target" />
                <ButtonAddContainer></ButtonAddContainer>
              </Container>
            </>
          )}
          <GroupedLoyaltyList
            onSelect={selectAlliance}
            onBack={handleCancelList}
            showList={showList}
            list={allAlliances}
          />
          <AddLoyalty
            userId={userId}
            showAdd={showAdd}
            alliance={selectedAlliance}
            onBack={cancelAdd}
            onAddLoyalty={handleAdd}
            isSubmitting={isSubmitting}
          />
          <EditLoyalty
            show={showEdit}
            remove={handleDelete}
            update={handleUpdate}
            updatePreference={handleUpdatePreference}
            userId={userId}
            idSelected={loyaltyEditId || ''}
            loyalties={loyalties}
            onBack={cancelEdit}
            isSubmitting={isSubmitting}
          />
        </>
      )}
    </MainContainer>
  );
};

export default TravelerLoyalty;
